import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { trackEvent } from "@/lib/analytics";
import {
  Home,
  Users,
  Briefcase,
  Star,
  TrendingUp,
  FileText,
  Clock,
  BarChart2,
  Menu,
  X,
  Sun,
  Moon,
  Settings,
  LogOut,
  Building2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { drawerVisibilityClass, useMobileDrawer } from "@/hooks/useMobileDrawer";
import { isCompanyEmployee } from "@/lib/companyRole";
import { isComplianceEnabled } from "@/lib/features";
import { MY_COMPANY_PATH } from "@/lib/compliance";
import { hasRole, getUserDisplayName } from "@/lib/auth";
import { ConsultantAvatar } from "@/components/consultants/ConsultantAvatar";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/button";
import Version from "@/components/Version";
import { EnvironmentHeaderMark } from "@/components/environment/EnvironmentBadge";
import { environmentMarkLabel } from "@/lib/environment";
import { ENV_HEADER_ACCENT_CLASS, ENV_MOBILE_TOGGLE_TOP_CLASS } from "@/lib/environmentMarkStyles";

const Navbar = () => {
  const { isOpen, close, drawerId, drawerRef, toggleProps } = useMobileDrawer();
  const [effectiveTheme, setEffectiveTheme] = useState<"light" | "dark">("light");
  const { setTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  // Non-production marks (#1099); PROD keeps the exact classes below.
  const envMarked = environmentMarkLabel() !== null;

  // Track which theme is currently applied to the document so we can show the
  // correct toggle icon and flip to the opposite theme on click. Watching the
  // root element via MutationObserver keeps us in sync with both explicit
  // toggles and "system" preference changes coming from ThemeProvider.
  useEffect(() => {
    const updateEffectiveTheme = () => {
      const root = document.documentElement;
      const isDark = root.classList.contains("dark");
      setEffectiveTheme(isDark ? "dark" : "light");
    };

    updateEffectiveTheme();

    const observer = new MutationObserver(updateEffectiveTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    // Simply toggle between light and dark, regardless of current theme setting
    const next = effectiveTheme === "dark" ? "light" : "dark";
    setTheme(next);
    trackEvent("theme_toggle", { theme: next });
  };

  const isAdmin = hasRole(user, "ADMIN");

  const navItems = [
    { title: "Dashboard", path: "/", icon: Home },
    { title: "Assignments", path: "/assignments", icon: Briefcase },
    { title: "Skills", path: "/skills", icon: Star },
    // An employee of a consultancy company sees no invoices (backend#1069);
    // the route redirects too (CompanyManagerRoute).
    ...(isCompanyEmployee(user)
      ? []
      : [{ title: "Financials", path: "/financials", icon: TrendingUp }]),
    ...(hasRole(user, "CONSULTANT")
      ? [
          { title: "My Engagements", path: "/consultant-engagements", icon: FileText },
          { title: "Time Tracking", path: "/time-tracking", icon: Clock },
          { title: "Hour Statistics", path: "/hour-statistics", icon: BarChart2 },
        ]
      : []),
    // My Company (#1132), before that Compliance and Policies (backend#894);
    // behind the compliance flag (BE #898). Consultants only: agreements,
    // documents and policies apply to consultants, and admins reach
    // compliance through Admin Portal → Compliance.
    ...(isComplianceEnabled(user) && hasRole(user, "CONSULTANT")
      ? [{ title: "My Company", path: MY_COMPANY_PATH, icon: Building2 }]
      : []),
    { title: "Settings", path: "/settings", icon: Settings },
    ...(isAdmin ? [{ title: "Admin Portal", path: "/admin", icon: Users }] : []),
  ];

  const isActive = (path: string) => {
    if (path === "/" && location.pathname === "/") {
      return true;
    }
    if (path !== "/" && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  return (
    <>
      {/* Mobile Navigation Toggle */}
      <div
        className={`fixed ${envMarked ? ENV_MOBILE_TOGGLE_TOP_CLASS : "top-4"} left-4 z-50 md:hidden`}
      >
        <Button
          variant="outline"
          size="icon"
          {...toggleProps}
          className="bg-background/80 backdrop-blur-sm"
        >
          {isOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </Button>
      </div>

      {/* Sidebar Navigation */}
      <aside
        id={drawerId}
        ref={drawerRef}
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-sidebar border-r border-sidebar-border shadow-lg transform ${drawerVisibilityClass(
          isOpen
        )} md:relative flex flex-col h-full overflow-y-auto`}
      >
        <div
          className={`p-4 border-b border-sidebar-border flex items-center justify-between${
            envMarked ? ` ${ENV_HEADER_ACCENT_CLASS}` : ""
          }`}
        >
          <Link to="/" className="flex items-center space-x-2.5">
            {/* All-teal wordmark reads on both the light and dark sidebar, so it
                needs no background plate (unlike the white-ink logo used on the
                dark auth pages and footer). */}
            <img
              src="/bondit-logo-wordmark-teal.png"
              alt="BondIT Consultancy"
              className="h-7 w-auto"
            />
            <span className="text-lg font-bold text-sidebar-foreground">Portal</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-8 w-8 text-sidebar-foreground hover:text-sidebar-foreground"
            aria-label={
              effectiveTheme === "dark" ? "Switch to light theme" : "Switch to dark theme"
            }
            title={effectiveTheme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {effectiveTheme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
          <EnvironmentHeaderMark />
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link flex items-center space-x-3 py-3 px-4 rounded-lg transition-colors ${
                isActive(item.path)
                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 font-medium shadow-sm"
                  : "hover:bg-sidebar-accent/50 text-sidebar-foreground"
              }`}
              onClick={close}
            >
              <item.icon
                className={`h-5 w-5 ${
                  isActive(item.path) ? "text-blue-700 dark:text-blue-300" : ""
                }`}
              />
              <span>{item.title}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-sidebar-border">
          <div className="flex items-center space-x-3 px-3 py-2 mb-2">
            <ConsultantAvatar
              consultantId={user?.consultant_profile?.id}
              name={getUserDisplayName(user)}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{getUserDisplayName(user)}</p>
              <p className="text-xs text-sidebar-foreground/70 truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              trackEvent("logout");
              logout();
            }}
            className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
          <div className="flex justify-center mt-2 pt-2 border-t border-sidebar-border/50">
            <Version className="text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground/80" />
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm transition-opacity duration-300 appearance-none border-0 p-0"
          onClick={close}
        />
      )}
    </>
  );
};

export default Navbar;
